import React from "react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  SendIcon,
  SkeletonCard,
} from "../../../components/ui";
import { lockedProps, type GateProps } from "../../billing/hooks/useSubscriptionGate";

/** The height the cards stand in at while a list is on its way — about three
 *  cards, which is what a phone shows before the fold. */
const CARDS_SKELETON = "h-[300px]";

/** One labelled figure under the rule — a macro, a count, a duration. */
export interface PlanCardFact {
  label: string;
  value: string;
  /** The chip beside the label, where a list keys its figures by colour. */
  color?: string;
}

/**
 * One plan as a phone reads it.
 *
 * Deliberately not any of the three summary types: the three lists are built
 * from different endpoints and agree about almost nothing, and what they do
 * agree about is exactly this — a name, a line of context, a state, and some
 * figures. Each panel maps its own rows into these, which is also where the
 * wording stays the list's own.
 */
export interface PlanCard {
  id: string;
  name: string;
  /** The line under the name, drawn dot-separated. Empty parts are dropped. */
  meta: string[];
  /** The pill at the end of that line, where the list has a state to report. */
  status?: { label: string; className: string };
  /** The row under the rule. */
  facts?: PlanCardFact[];
  /** What is on the plan by name, where that is a list rather than figures. */
  chips?: string[];
  /** What the send button is called out loud — a phone's send button is a
   *  glyph, and "Send" three times down a list names nothing. */
  sendLabel: string;
}

export interface PlanSendCardsProps {
  plans: PlanCard[];
  /** Stands in for the cards when the list comes back with none. */
  empty: EmptyStateProps;
  /** Where the list is — the same contract the tables work to. */
  phase?: LoadPhase;
  /** Off with its reason while the workspace has no plan behind it: sending
   *  is what a subscription buys. */
  gate: GateProps;
  onSend: (planId: string) => void;
}

/**
 * A client's plans as they read on a phone: one card per plan, and one thing
 * to do to it.
 *
 * The three tables this stands in for need 820–900px for their columns, and
 * what they put in those columns is a row number, a name, some figures, a
 * state and a menu of four actions. On a phone the number has nothing to find,
 * and three of the four actions are gone with it: creating a plan and opening
 * one both lead into the builder, which the app does not carry at this width,
 * and duplicating is the builder by another door. What is left is the one
 * thing a dietitian does to a finished plan from a phone — send it to the
 * client — so that is the card's only control, and it is a target rather than
 * an entry in a menu behind one.
 *
 * Shared by all three lists rather than written per kind: a practitioner
 * switching Meal → Supplement → Exercise is looking at one list that changes
 * what it holds, and three cards that each decided their own corners would
 * read as three screens. What differs between them is the figures, which is
 * what `PlanCard` leaves to the caller.
 */
const PlanSendCards: React.FC<PlanSendCardsProps> = ({
  plans,
  empty,
  phase = "ready",
  gate,
  onSend,
}) => {
  if (phase !== "ready" && plans.length === 0) {
    return (
      <div className="flex-1 min-h-0">
        <SkeletonCard phase={phase} className={CARDS_SKELETON} />
      </div>
    );
  }

  if (plans.length === 0) {
    // A column rather than a row, so the state gets the full width to set its
    // lines in. It brings its own padding.
    return (
      <div className="flex-1 min-h-0 flex flex-col justify-center">
        <EmptyState {...empty} />
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-overlay">
      <ul className="flex flex-col gap-3 pb-4">
        {plans.map((plan) => (
          <li key={plan.id}>
            <div className="card-surface border border-line bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                {/* `min-w-0` is what lets a long plan name shorten instead of
                    pushing the send button off the card's edge. */}
                <div className="min-w-0">
                  <h3 className="break-words text-[16px] font-medium text-ink">
                    {plan.name}
                  </h3>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-ink-muted">
                    {plan.meta
                      .filter(Boolean)
                      .map((part, index) => (
                        <React.Fragment key={`${part}-${index}`}>
                          {index > 0 && (
                            <span aria-hidden="true" className="text-ink-subtle">
                              ·
                            </span>
                          )}
                          <span>{part}</span>
                        </React.Fragment>
                      ))}
                    {plan.status && (
                      <span
                        className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${plan.status.className}`}
                      >
                        {plan.status.label}
                      </span>
                    )}
                  </div>
                </div>

                {/* A filled target rather than a glyph on the card's ground:
                    it is the only thing on the card that does anything, and
                    the thumb reaching for it is doing so without reading. Not
                    a `Button` — it carries no words, so it takes the gate's
                    answers apart itself. See `lockedProps`. */}
                <button
                  type="button"
                  aria-label={plan.sendLabel}
                  {...lockedProps(gate, () => onSend(plan.id))}
                  // The same pale fill a `Button` goes to when it is shut, so
                  // a send that cannot happen looks the same here as it does
                  // on the buttons beside it.
                  className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${
                    gate.disabled
                      ? "cursor-not-allowed bg-[#D7F9FD] text-[#00BACB]"
                      : "bg-brand text-white hover:bg-brand-hover"
                  }`}
                >
                  {/* The design's own plane — Hugeicons `navigation-03`, drawn
                      as it comes rather than turned. See `SendIcon`. */}
                  <SendIcon className="h-[18px] w-[18px]" />
                </button>
              </div>

              {!!plan.facts?.length && (
                <dl className="mt-3 grid grid-cols-4 gap-x-2 gap-y-3 border-t border-line pt-3">
                  {plan.facts.map((fact) => (
                    <div key={fact.label} className="min-w-0">
                      <dt className="flex items-center gap-1.5 text-[13px] text-ink-muted">
                        {fact.color && (
                          <span
                            aria-hidden="true"
                            className="h-3 w-[3px] flex-shrink-0 rounded-full"
                            style={{ backgroundColor: fact.color }}
                          />
                        )}
                        <span className="truncate">{fact.label}</span>
                      </dt>
                      <dd className="mt-0.5 text-[15px] text-ink">
                        {fact.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}

              {/* Every one of them, wrapped onto as many lines as it takes:
                  what is on the plan is the only thing that tells two of a
                  client's supplement plans apart, and a +n count standing for
                  the rest would hide exactly that. The table leans on
                  `OneLineList` because it has one line; a card has as many as
                  the names need. */}
              {!!plan.chips?.length && (
                <div
                  className={`flex flex-wrap gap-2 ${
                    plan.facts?.length ? "mt-3" : "mt-3 border-t border-line pt-3"
                  }`}
                >
                  {plan.chips.map((name, index) => (
                    <span
                      key={`${name}-${index}`}
                      className="inline-flex rounded-md bg-brand-soft px-2.5 py-1 text-[12px] font-medium text-brand-dark"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PlanSendCards;
